import bilAPI from "./api";

export const getObservations = ({ params = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/`, { params, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// `role` picks which side of the visit you are asking about:
//   main_observer — visits you ran (you are the MAIN observer)
//   observed      — visits of your own lessons
// Omitted, it returns both plus visits you merely created.
export const getMyObservations = ({
  params: { page, role, status__in, ordering, observed_teacher__uuid } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/mine/`, {
          params: { page, role, status__in, ordering, observed_teacher__uuid },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getUpcomingObservations = ({
  params: { page, page_size = 200, include_overdue } = {},
  signal,
} = {}) => {
  // The Upcoming tab renders a full multi-week calendar + list, so it needs the
  // whole upcoming set, not just the default 20-row first page. page_size is an
  // opt-in honoured by StandardResultsSetPagination (capped at 1000).
  //
  // `include_overdue` keeps past-dated bookings the endpoint otherwise drops.
  // The tab asks for them because it is the only place the person who owes the
  // visit can reach it — without them the Conduct action is invisible on
  // exactly the visits that need it.
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/upcoming/`, {
          params: { page, page_size, include_overdue },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// The recommendation threshold is server-side by design — a caller-chosen
// cutoff would be a binary-search oracle on colleagues' hidden scores.
export const getPeerExperts = ({
  params: { standards } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/peer-experts/`, {
          params: { standards },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getObservation = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Per-period availability for one teacher on one date: their lessons that
// weekday, the visit already sitting on each slot, and why the observer cannot
// make it. `observer` defaults server-side to the caller; `observation` excludes
// one booking from the clash set so a reschedule does not report itself.
//
// One request rather than three (timetable + this teacher's visits + the
// observer's day), because the three have to agree about one moment in time and
// the rule for what "free" means belongs on the server next to the guard that
// enforces it.
export const getSlotAvailability = ({
  params: { teacher, date, observer, observation } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/slots/`, {
          params: { teacher, date, observer, observation },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const createObservation = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .patch(`/observations/${uuid}/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteObservation = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/observations/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const submitObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/submit/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const cancelObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/cancel/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const confirmObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/confirm/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const declineObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/decline/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const shareObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/share/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const acknowledgeObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/acknowledge/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getObservationFeedback = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/${uuid}/feedback/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const saveObservationFeedback = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/feedback/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Append-only note on a submitted visit. Resolves to the full detail payload,
// so the caller can swap its cached copy wholesale.
export const addObservationAddendum = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/addenda/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const respondToObservation = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/observations/${uuid}/respond/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getFocusAreas = ({
  params: { teacher, window_days, observation_limit } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/observations/focus-areas/`, {
          params: { teacher, window_days, observation_limit },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
